"use client";

import type { FC } from "react";
import { useEffect } from "react";
import { motion } from "motion/react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { VALUE_PROP_COPY } from "../onboardingWizardCopy";
import { OnboardingResponsiveCtaFooter } from "../shared/OnboardingResponsiveCtaFooter";
import { ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS } from "../shared/onboardingFloatingCtaTokens";
import { ValuePropHeadlineWatermark } from "./ValuePropHeadlineWatermark";
import { trackObValuePropView } from "@/src/hooks/onboarding/onboardingAnalytics";

interface ValuePropStepProps {
  onContinue: () => void;
}

const STEP_ICONS = ["check", "edgen-radar-outline", "flag"] as const;
const STEP_ICON_BG = [
  "bg-content_brand_primary",
  "bg-content_brand_primary",
  "bg-content_accent4_primary",
] as const;

/** Figma 97267:68495 — entrance within 900ms */
const FADE_UP = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
} as const;
const DURATION_S = 0.24;
const EASE = "easeOut" as const;
/** Headline starts at 0; steps after headline with 100ms stagger; CTA last. */
const HEADLINE_DELAY_S = 0;
const STEP0_DELAY_S = 0.24;
const STEP_STAGGER_S = 0.1;
const CTA_DELAY_S = 0.66;

export const ValuePropStep: FC<ValuePropStepProps> = ({ onContinue }) => {
  useEffect(() => {
    trackObValuePropView();
  }, []);

  return (
    <div
      className="relative flex min-h-dvh max-md:h-dvh flex-col overflow-x-hidden bg-background_solid_neutral_secondary max-md:overflow-y-hidden md:overflow-y-auto no-scrollbar"
      data-testid="onboarding-value-prop-step"
      data-figma-node="96118:8348"
      data-figma-node-pc="96344:110167"
      data-layout="value-prop-responsive"
    >
      <div
        className={cn(
          // H5: flex-1 + min-h-0 scroll under floating CTA.
          // PC: Figma 96344:110167 — exact 460px column, no horizontal inset (px-4 was shrinking content).
          "flex max-md:min-h-0 max-md:flex-1 flex-col overflow-x-hidden max-md:overflow-y-auto px-6 md:mx-auto md:w-[460px] md:max-w-[460px] md:shrink-0 md:px-0 md:pt-16 no-scrollbar",
          ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS,
        )}
        data-testid="value-prop-scroll-column"
      >
        <motion.div
          className="relative min-h-[174px] max-md:-mx-6 max-md:mt-[84px] max-md:pt-5"
          initial={FADE_UP.initial}
          animate={FADE_UP.animate}
          transition={{
            duration: DURATION_S,
            delay: HEADLINE_DELAY_S,
            ease: EASE,
          }}
          data-testid="value-prop-headline-motion"
        >
          <ValuePropHeadlineWatermark />
          <h1
            className="headline_regular_xl md:display_xs relative z-[1] text-content_primary max-md:px-6 md:pt-5 whitespace-pre-line"
            data-testid="value-prop-headline"
          >
            <span className="block">{VALUE_PROP_COPY.headlineLine1}</span>
            <span className="block">{VALUE_PROP_COPY.headlineLine2}</span>
            <span className="block text-content_accent4_primary">
              {VALUE_PROP_COPY.headlineAccent}
            </span>
          </h1>
        </motion.div>

        <div
          className="relative mt-20 flex flex-col md:mt-10"
          data-testid="value-prop-steps"
        >
          <motion.div
            className="pointer-events-none absolute left-[22px] top-12 z-0 w-1 origin-top rounded-sm bg-background_brand_overlay10"
            aria-hidden
            data-testid="value-prop-timeline"
            initial={{ height: 0 }}
            animate={{ height: 192 }}
            transition={{
              duration: STEP_STAGGER_S * 2 + DURATION_S,
              delay: STEP0_DELAY_S,
              ease: EASE,
            }}
          />

          {VALUE_PROP_COPY.steps.map((item, index) => (
            <motion.div
              key={item.title}
              className={cn(
                "relative flex items-start gap-4",
                index < VALUE_PROP_COPY.steps.length - 1 && "h-[120px]",
              )}
              data-testid={`value-prop-step-${index}`}
              initial={FADE_UP.initial}
              animate={FADE_UP.animate}
              transition={{
                duration: DURATION_S,
                delay: STEP0_DELAY_S + index * STEP_STAGGER_S,
                ease: EASE,
              }}
            >
              <div
                className={cn(
                  "relative z-[1] flex size-12 shrink-0 items-center justify-center rounded-full",
                  STEP_ICON_BG[index],
                )}
                data-testid={`value-prop-step-${index}-icon`}
              >
                <Icon
                  name={STEP_ICONS[index]}
                  size={24}
                  className="text-content_inverse_primary"
                />
              </div>
              <div
                className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5"
                data-testid={`value-prop-step-${index}-text`}
              >
                <p
                  className="title_l text-content_primary"
                  data-testid={`value-prop-step-${index}-title`}
                >
                  {item.title}
                </p>
                <p
                  className="body_medium_m text-content_secondary"
                  data-testid={`value-prop-step-${index}-body`}
                >
                  {item.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <OnboardingResponsiveCtaFooter inset="wide" testId="value-prop-footer">
        <motion.div
          className="w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: DURATION_S,
            delay: CTA_DELAY_S,
            ease: EASE,
          }}
          data-testid="value-prop-cta-motion"
        >
          <button
            type="button"
            data-testid="value-prop-lets-go"
            className="button md_button primary_button button_m h-12 w-full rounded-lg text-content_inverse_primary"
            onClick={onContinue}
          >
            {VALUE_PROP_COPY.cta}
          </button>
        </motion.div>
      </OnboardingResponsiveCtaFooter>
    </div>
  );
};
